import { parseSPDate } from './format/data';
import { formatMoeda } from './format/moeda';
import { TOLERANCIA_CENTAVO_CALIBRACAO, descalibrarTotalCalibrado } from './desconto';

// Denominacoes do Real pra gradinha de contagem do caixa (Onda Caixa Rotativo / DA-117).
// A chave da gradinha (jsonb) e o valor em CENTAVOS como string; o valor e a quantidade contada.
export interface Denominacao {
  cents: number;
  label: string;
  tipo: 'nota' | 'moeda';
}

export const DENOMINACOES_BRL: Denominacao[] = [
  { cents: 20000, label: 'R$ 200', tipo: 'nota' },
  { cents: 10000, label: 'R$ 100', tipo: 'nota' },
  { cents: 5000, label: 'R$ 50', tipo: 'nota' },
  { cents: 2000, label: 'R$ 20', tipo: 'nota' },
  { cents: 1000, label: 'R$ 10', tipo: 'nota' },
  { cents: 500, label: 'R$ 5', tipo: 'nota' },
  { cents: 200, label: 'R$ 2', tipo: 'nota' },
  { cents: 100, label: 'R$ 1', tipo: 'moeda' },
  { cents: 50, label: 'R$ 0,50', tipo: 'moeda' },
  { cents: 25, label: 'R$ 0,25', tipo: 'moeda' },
  { cents: 10, label: 'R$ 0,10', tipo: 'moeda' },
  { cents: 5, label: 'R$ 0,05', tipo: 'moeda' },
];

// Status de orcamento que ja virou VENDA (finalizado) ou foi cancelado. Nesses o orcamento e a
// FONTE da nota fiscal (Caminho B: a emissao le total/itens/pagamentos do orcamento ao vivo), entao
// editar aqui desalinharia a nota do que foi cobrado no caixa. Trava a edicao manual no front E no
// backend (regra unica). Corrigir = reabrir a venda no Caixa (cancelar/estornar), que devolve o
// orcamento a 'enviado_caixa' e o reabre. NAO confundir com o conjunto FROZEN do reajuste de
// preco-vivo, que tambem inclui 'enviado_caixa' — esse status segue EDITAVEL (correcao no caixa
// antes de fechar, calibracao DA-233).
export function orcamentoTravadoParaEdicao(status: string | null | undefined): boolean {
  // 'compra_credito': o cliente levou o orcamento pagando com credito em Trocas e Devolucoes
  // (migration 0340) — a mercadoria saiu e o credito foi gasto; mudar os itens desalinharia os dois.
  return status === 'finalizado' || status === 'cancelado' || status === 'compra_credito';
}

// Dia calendario SP de um instante, no formato 'YYYY-MM-DD'. O 'en-CA' rende ISO por padrao.
function diaSP(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

// Vencimento do crediario = data de EMISSAO do orcamento (`criado_em`) + N dias, com PISO em hoje.
//
// Antes o prazo contava de `CURRENT_DATE` (backend) / `new Date()` (front), entao um pedido emitido
// dia 13 e finalizado no caixa dia 18 dava ao cliente 15 dias a partir do 18 — o prazo esticava
// sozinho conforme o pedido esperava na fila. A ancora e a emissao: o prazo que o vendedor prometeu
// comeca a correr quando a venda foi feita, nao quando a operadora conseguiu fechar.
//
// PISO EM HOJE: pedido parado tempo demais (emissao + dias ja no passado) venceria retroativo e
// nasceria ja atrasado no Contas a Receber, poluindo a lista de inadimplencia com divida que o
// cliente nunca teve chance de pagar. Nesse caso vence hoje.
//
// ⚠️ **A ANCORA MUDOU em 27/08/2026** ([DA-631]): quem manda agora e' `enviado_caixa_em`, o
// carimbo do ENVIO AO CAIXA — ver `ancoraDoVencimento` logo abaixo, que e' quem escolhe.
// Ate entao era `criado_em` ([DA-335]), o nascimento do pedido.
//
// Devolve 'YYYY-MM-DD' (dia puro, sem hora) — o backend grava direto em `contas_receber.data_vencimento`
// e o front formata pra exibir. Regra UNICA dos dois lados pra data mostrada ser a data gravada.
/** O pedido, pelo que a ancora do vencimento precisa saber dele. */
export interface PedidoParaVencimento {
  /** Quando foi PRO CAIXA (migration 0257). `null`/ausente em pedido anterior a ela. */
  enviado_caixa_em?: string | null;
  /** Quando o pedido nasceu. */
  criado_em: string;
}

/**
 * De QUANDO o prazo do crediario comeca a correr ([DA-631], 27/08/2026).
 *
 * 🔑 **Do ENVIO AO CAIXA** — decisao dele, nestas palavras: *"ao finalizar, os 15 dias
 * devem contar a partir dela"*, apontando a coluna DATA da aba "Pedidos pra finalizar".
 *
 * ⚠️ **Isto e' uma FUNCAO e nao um `??` escrito duas vezes de proposito.** A conta vive
 * no backend (que GRAVA o titulo) e no front (que MOSTRA a data antes de finalizar); com
 * duas copias, mudar uma so faz a data que a operadora ve deixar de ser a que vai pro
 * Contas a Receber — e nada falha, ninguem percebe.
 *
 * ⚠️ **O fallback nao inventa dado**: pedido enviado ao caixa ANTES da migration 0257 nao
 * tem carimbo (o `atualizado_em` dele ja tinha virado a hora da finalizacao — medido em 10
 * de 10 vendas), entao cai no nascimento do pedido. E' a melhor aproximacao existente e ela
 * foi MEDIDA: nos 7 pedidos que estavam na fila em 27/08/2026, nascer e ir pro caixa foi o
 * **mesmo dia em 7 de 7**.
 *
 * ⛔ **Nao voltar a usar `atualizado_em`** — a [DA-335] o rejeitou com razao (ele se move a
 * cada EDICAO, entao pedido devolvido e reenviado resetava o prazo) e, pior, na finalizacao
 * ele **vira a hora de fechar**, que e' exatamente o defeito que esta peca existe pra matar.
 */
export function ancoraDoVencimento(pedido: PedidoParaVencimento): string {
  // ⚠️ Nem `??` nem `||` sozinhos servem: o `??` deixaria a string VAZIA passar (e um
  // carimbo vazio vira data invalida, empurrando o prazo pro fallback de "hoje + N" la
  // dentro, que e' o bug antigo de volta), e o `||` trataria `'0'` como ausencia. O teste
  // trava os tres casos.
  const carimbo = pedido.enviado_caixa_em;
  return typeof carimbo === 'string' && carimbo.trim() !== '' ? carimbo : pedido.criado_em;
}

export function calcularVencimentoCrediario(
  criadoEm: string | Date | null | undefined,
  dias: number,
  agora: Date = new Date(),
): string {
  const hoje = diaSP(agora);
  const n = Number.isFinite(dias) ? Math.trunc(dias) : 0;

  const base = parseSPDate(criadoEm);
  // Sem emissao legivel, o unico chao confiavel e hoje (comportamento antigo).
  if (!base) return diaSP(new Date(agora.getTime() + Math.max(n, 0) * 86400000));

  // Soma em cima do DIA calendario (nao do instante), pra hora de emissao nao empurrar a virada.
  const [y, mo, d] = diaSP(base).split('-').map(Number);
  const venc = new Date(Date.UTC(y!, mo! - 1, d!));
  venc.setUTCDate(venc.getUTCDate() + n);
  const vencISO = venc.toISOString().slice(0, 10);

  return vencISO < hoje ? hoje : vencISO;
}

// Confirmacao por DIGITACAO do saldo no "Fechar caixa sem contagem" (DA-395). O fechamento sem
// contagem grava o saldo calculado como afirmacao humana, e ate 30/07/2026 bastavam dois cliques
// no MESMO rotulo ("Fechar caixa" na linha abre o modal, "Fechar caixa" no rodape executa) — o
// gesto nao PARECIA confirmar nada, e o Ruan fechou o Movimento de Caixa achando que nada tinha
// sido perguntado. Agora o botao so libera quando ele redigita o numero: a prova de que leu.
//
// Aceita o valor SEM o sinal quando o esperado e negativo (o "Movimento de Caixa" fecha em
// -R$ 4.104,17 e obrigar a digitar o menos so treina a copiar sem ler). Tolerancia de meio
// centavo pelo mesmo motivo de sempre: numero em ponto flutuante nao se compara por igualdade.
export function confirmacaoSaldoBate(digitado: string | null | undefined, esperado: number | null | undefined): boolean {
  if (esperado == null || !Number.isFinite(esperado)) return false;
  const t = String(digitado ?? '').trim();
  if (t === '') return false;
  // Tira "R$", espacos e o sinal; o que sobra tem que ser so digito/separador.
  const semSinal = t.replace(/^R\$\s*/i, '').replace(/^-\s*/, '').trim();
  if (!/^\d[\d.,]*$/.test(semSinal)) return false;
  let n: string;
  if (semSinal.includes(',')) n = semSinal.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(semSinal)) n = semSinal.replace(/\./g, '');
  else n = semSinal;
  const v = Number(n);
  if (!Number.isFinite(v)) return false;
  return Math.abs(Math.abs(v) - Math.abs(esperado)) < 0.005;
}

const CENTS_VALIDOS = new Set(DENOMINACOES_BRL.map((d) => d.cents));

// Soma a gradinha (cents->qtd) em reais. Ignora chaves desconhecidas e qtd negativa/invalida.
// Fonte unica usada no backend (nao confia no total do cliente) e no frontend (preview).
export function totalGradinha(gradinha: Record<string, number> | null | undefined): number {
  if (!gradinha) return 0;
  let totalCents = 0;
  for (const [k, v] of Object.entries(gradinha)) {
    const cents = Number(k);
    const qtd = Number(v);
    if (!CENTS_VALIDOS.has(cents) || !Number.isFinite(qtd) || qtd <= 0) continue;
    totalCents += cents * Math.floor(qtd);
  }
  return totalCents / 100;
}

// =============================================================================
// TRAVA DO PEDIDO NO CAIXA (DA-399, cartao RDS caixa #12)
// =============================================================================
// Pedido que depende de um ajuste ainda nao resolvido pode ser TRAVADO por qualquer pessoa, com
// motivo obrigatorio. Travado, o pedido nao finaliza, nao volta pro orcamento e nao e excluido.
// A regra de QUEM pode destravar mora aqui porque vale nos dois lados: o front decide se mostra o
// cadeado aberto, o backend recusa de verdade. Duas copias divergiriam no primeiro ajuste.

// O dono da loja e identificado pelo LOGIN, nao por id nem por flag nova — mesmo padrao ja usado
// no Leitor de Voz (ler.routes.ts) e no BYPASS_LOGINS do device-trust. Mudou o login, muda aqui so.
export const LOGIN_DONO = 'RUAN';

export interface PedidoTravaInfo {
  travado_por: number | null;
  travado_por_login: string | null;
}

export interface UsuarioTrava {
  id: number;
  perfil: string | null;
  login?: string | null;
}

// Quem destrava: a propria pessoa que travou, ou qualquer admin.
// EXCECAO pedida pelo Ruan: trava posta pelo DONO so o DONO tira — nem outro admin passa por cima.
// Trava sem autor conhecido (dado legado) cai no caso comum: admin resolve.
export function podeDestravarPedido(trava: PedidoTravaInfo, usuario: UsuarioTrava): boolean {
  if (trava.travado_por !== null && trava.travado_por === usuario.id) return true;
  if ((trava.travado_por_login ?? '').trim().toUpperCase() === LOGIN_DONO) return false;
  return usuario.perfil === 'admin';
}

// Motivo e obrigatorio: e o que transforma a trava em recado. Sem ele o proximo admin destrava so
// pra tocar a fila e a trava morre de morte natural.
export function motivoTravaValido(motivo: string | null | undefined): boolean {
  return (motivo ?? '').trim().length >= 3;
}

// As acoes que mexem num pedido do caixa. Uma lista so, lida pela trava manual (DA-399) e pela
// trava do Pix vinculado (DA-560) — as duas recusam pelas mesmas portas e falam com a mesma pessoa.
export type AcaoNoPedido =
  | 'finalizar a venda'
  | 'devolver ao orcamento'
  | 'excluir o pedido'
  | 'editar o pedido';

// Texto UNICO da recusa nas tres portas de saida — o operador precisa saber quem travou e por que
// sem sair da tela, senao a trava vira cadeado mudo.
export function mensagemPedidoTravado(
  motivo: string | null | undefined,
  porNome: string | null | undefined,
  acao: AcaoNoPedido,
): string {
  const quem = porNome ? ` por ${porNome}` : '';
  const texto = (motivo ?? '').trim() || 'sem motivo registrado';
  return `Pedido TRAVADO${quem}: ${texto} — destrave antes de ${acao}.`;
}

// --- Pedido com Pix JA VINCULADO ---  [DA-560]
//
// O vinculo Pix<->pedido e uma amarra entre o DINHEIRO e AQUELE pedido, e ele sobrevivia a tudo que
// muda o pedido: devolver ao orcamento, editar no caixa, excluir. Sobrevivendo, ele segue afirmando
// "este Pix pagou este pedido" sobre um pedido que ja nao e o mesmo. Caso que originou (ORC-006767,
// 15/08/2026): Pix de 110,01 vinculado as 09:53:09, pedido devolvido ao orcamento as 09:53:14 e
// refeito valendo 73,01 — o dinheiro do cliente ficou preso num pedido que nao era o dele, e a
// unica saida foi mexer no banco a mao.
//
// A recusa NOMEIA o caminho de volta de proposito: a trava so e legitima porque existe quem a
// desfaca (o admin, na Conciliacao Bancaria). Trava sem escape e beco, nao protecao ([DA-547]).
// `qtdVinculos` aceita NULL de proposito: a tela do Caixa sabe QUANTO esta vinculado por pedido
// (`valor_pix_vinculado`) mas nao quantos recebimentos sao, e pedir esse numero ao backend so pra
// enfeitar a frase custaria uma consulta a mais em toda abertura da fila. Sem a contagem a frase
// perde o numeral e continua inteira. O que NAO se faz e escrever um segundo texto pro front: a
// recusa da tela e a do servidor tem que ser a MESMA, senao elas envelhecem separadas.
export function mensagemPedidoComPixVinculado(
  totalVinculado: number,
  qtdVinculos: number | null,
  acao: AcaoNoPedido,
): string {
  const valor = totalVinculado.toFixed(2).replace('.', ',');
  const quantos = qtdVinculos === null ? 'Pix' : qtdVinculos === 1 ? 'um Pix' : `${qtdVinculos} Pix`;
  return `Este pedido tem ${quantos} ja vinculado, somando R$ ${valor} — o dinheiro esta amarrado a ele. `
       + `Um admin precisa desvincular o Pix na aba Conciliacao Bancaria do Caixa antes de ${acao}.`;
}

// --- Devolver ao orcamento SOLTA o Pix ---  [DA-662]
//
// A porta do "voltar" deixou de RECUSAR e passou a RESOLVER. O que derrubou a recusa nao foi o
// numero de cliques: foi ela mandar a vendedora fazer uma coisa que ela nao tem permissao de fazer
// — o texto acima diz "um admin precisa desvincular", e desvincular JA era so-admin. Ou seja, a
// trava ja obrigava o admin a entrar; ela so o fazia entrar em DOIS lugares. Agora a permissao E a
// trava, no lugar onde a decisao esta sendo tomada.
//
// As outras duas portas (editar no caixa, excluir) seguem com `mensagemPedidoComPixVinculado`. A
// assimetria e deliberada: quem VOLTA esta tirando o pedido do caixa — ele vira rascunho do
// vendedor —, enquanto quem EDITA mantem o pedido no caixa, onde aquele dinheiro ainda pode fechar
// a venda. Soltar ali tiraria o lastro de uma venda que ainda vai acontecer.
export function mensagemPixVinculadoSoAdmin(
  totalVinculado: number,
  qtdVinculos: number | null,
): string {
  const valor = totalVinculado.toFixed(2).replace('.', ',');
  const quantos = qtdVinculos === null ? 'Pix' : qtdVinculos === 1 ? 'um Pix' : `${qtdVinculos} Pix`;
  return `Este pedido tem ${quantos} ja vinculado, somando R$ ${valor} — o dinheiro esta amarrado a ele. `
       + `Devolver ao orcamento SOLTA esse dinheiro, e soltar Pix e acao de admin. `
       + `Peca a um admin pra devolver este pedido.`;
}

// A confirmacao do admin. Ela existe porque a trava antiga fazia UMA coisa bem: obrigava a soltar o
// dinheiro OLHANDO pro dinheiro, na Conciliacao. Resolvendo aqui, o admin decide de costas pra ele
// — entao a frase precisa dizer o VALOR e ONDE ele vai parar, e dizer isso ANTES do clique.
//
// A ordem das frases e a ordem dos gestos, nao a ordem de importancia: primeiro o que o botao vai
// fazer com o dinheiro, depois o que ele NAO faz. Ressalva escrita depois da acao chega depois do
// clique, que e como o operador le.
export function confirmacaoSoltarPixAoVoltar(
  totalVinculado: number,
  qtdVinculos: number | null,
  numeroPedido: string,
): string {
  const valor = totalVinculado.toFixed(2).replace('.', ',');
  const quantos = qtdVinculos === null ? 'um Pix vinculado' : qtdVinculos === 1 ? 'um Pix vinculado' : `${qtdVinculos} Pix vinculados`;
  return `Devolver o pedido ${numeroPedido} ao vendedor?\n\n`
       + `Este pedido tem ${quantos}, somando R$ ${valor}. Ao confirmar, o sistema SOLTA esse Pix: `
       + `ele volta pra aba Conciliacao Bancaria como recebimento a conciliar, livre pra ser `
       + `vinculado no pedido certo.\n\n`
       + `O dinheiro NAO volta pro cliente — ele continua na conta da loja. Devolver ao cliente e `
       + `acao separada, na propria Conciliacao.`;
}

// --- Linha de pagamento: VALOR x DESCONTO (R$ e %) ---  [DA-403]
//
// Uma linha carrega tres numeros amarrados: BRUTO (o que ela cobraria sem desconto), DESCONTO (o
// que o operador perdoou nela) e VALOR (o liquido que entra no caixa). A invariante e uma so:
//
//     valor + desconto === bruto      (e 0 <= desconto <= bruto)
//
// O `bruto` PRECISA ser estado (nao da pra derivar de valor+desconto no meio de uma edicao de
// valor, senao a conta gira em circulo). Ate 30/07/2026 ele era congelado na 1a mexida e NUNCA
// crescia: digitar no VALOR um numero maior que o bruto travado era lido como "aumento de preco" e
// batia no teto de 10% da LINHA. Deu erro num caso trivial — a linha valia 1,00, o operador quis
// cobrar 1,21 (o que de fato faltava) e o sistema recusou dizendo "aumento maximo: R$ 1,10".
//
// Regra ate 19/08/2026: subir o VALOR subia o bruto junto; DESCER o valor mantinha o bruto e virava
// DESCONTO (definicao do Ruan de 15/07/2026). ⛔ REVOGADA — o campo VALOR agora so REALOCA.
//
// O que ela custava: com uma unica linha cobrindo o total, nao havia como dividir o pagamento. O
// operador que quisesse cobrar 175,00 no Pix de uma venda de 420,01 baixava a linha e o sistema
// entendia desconto de 245,01 — a venda INTEIRA virava 175,00. E o "Adicionar forma de pagamento"
// fica desabilitado enquanto nao falta valor, entao ele orientava ("baixe o valor de uma linha")
// exatamente o ato que fechava a porta. Beco medido no ensaio em 19/08/2026 ([DA-582]).
//
// Regra nova: o VALOR e ALOCACAO pura — quanto desta venda entra por esta forma. Baixar deixa o
// resto FALTANDO (o painel mostra FALTA, o botao de adicionar forma acende, os botoes de nota
// travam ate cobrir); subir aloca mais. O DESCONTO tem campo proprio (`Desc. R$` / `%`) e so sai
// por ele — inclusive o portao de senha gerencial acima de 5%, que era furado por quem digitava
// direto no Valor. Erro de digitacao segue barrado por `acrescimoAcimaDoTeto` (teto de +10% sobre
// o preco de tabela) e por `classificarPagamentoFechamento`, que mede o pago contra o TOTAL.

export interface LinhaPagamentoValores {
  /** Liquido cobrado nesta linha (o que entra no caixa). */
  valor: number;
  /** Quanto foi perdoado nesta linha, em R$. */
  desconto: number;
  /** Alocacao da linha antes do desconto. Sempre valor + desconto. */
  bruto: number;
}

const cent = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

/** Bruto atual de uma linha (o que ela cobraria sem desconto). */
export function brutoDaLinha(valor: number, desconto: number): number {
  return cent(Math.max(0, cent(valor)) + Math.max(0, cent(desconto)));
}

/**
 * Operador digitou o VALOR cobrado: ALOCACAO pura. O desconto ja registrado na linha (campo
 * proprio) e preservado e o bruto acompanha, mantendo a invariante `valor + desconto === bruto`.
 * Descer NAO gera desconto — deixa o resto faltando, pra ser coberto por outra forma. [DA-582]
 */
export function realocarValorDaLinha(descontoAtual: number, novoValor: number): LinhaPagamentoValores {
  const valor = Math.max(0, cent(novoValor));
  const desconto = Math.max(0, cent(descontoAtual));
  return { valor, bruto: cent(valor + desconto), desconto };
}

/** Operador digitou o DESCONTO em R$. O bruto nao se mexe; o valor e o que sobra dele. */
export function editarDescontoDaLinha(brutoAtual: number, novoDesconto: number): LinhaPagamentoValores {
  const bruto = Math.max(0, cent(brutoAtual));
  const desconto = Math.min(Math.max(0, cent(novoDesconto)), bruto);
  return { valor: cent(bruto - desconto), bruto, desconto };
}

/** Desconto em % sobre o bruto (0 quando a linha e zerada). */
export function pctDescontoDaLinha(bruto: number, desconto: number): number {
  const b = cent(bruto);
  if (!(b > 0)) return 0;
  return Math.round((cent(desconto) / b) * 10000) / 100;
}

/**
 * Operador digitou o desconto em %. Converte pra R$ e cai na mesma regra do desconto.
 *
 * Arredonda o centavo PRA BAIXO (floor, nao round): desconto e "ate X%", nunca mais que X% — a
 * mesma doutrina do `arredondarTotalDesconto`, que sobe o total em favor da loja. Com `round`,
 * 50% de R$ 1,21 (= R$ 0,605) virava R$ 0,61 e a tela exibia 50,41% pra quem digitou 50; pior,
 * um 5% digitado podia estourar o teto de 5,1% e pedir senha gerencial sem que ninguem tivesse
 * pedido mais que 5%.
 */
export function editarDescontoPctDaLinha(brutoAtual: number, pct: number): LinhaPagamentoValores {
  const bruto = Math.max(0, cent(brutoAtual));
  const p = Math.min(Math.max(0, Number(pct) || 0), 100);
  return editarDescontoDaLinha(bruto, Math.floor(bruto * (p / 100) * 100) / 100);
}

// =============================================================================
// QUANTO DE PIX ESTE PEDIDO ESPERA (DA-431)
// =============================================================================
// A fila do caixa mede o Pix de um pedido pelo DECLARADO em `orcamento_pagamentos`. Isso vale pro
// pedido comum — mas pedido que voltou de venda CANCELADA nao tem declaracao nenhuma: o cancelar
// apaga a tabela de proposito (senao a venda desfeita somaria comissao ao vendedor, DA-032) e sobra
// so o `mp_vinculo` do Pix, que fica de pe porque o dinheiro ENTROU e nao foi devolvido (devolver
// tem porta propria, DA-206).
//
// Sem esta regra a coluna PIX mostrava "—" num pedido com Pix recebido e conferido — igual a um que
// ninguem pagou. Quem pegasse o pedido na fila podia cobrar o cliente de novo, ou fechar em dinheiro
// e deixar o vinculo lastreando uma venda sem Pix.
//
// A base do caso sem declaracao e a LINHA de Pix congelada (`valor_pix_unico`, DA-320), NAO o total:
// numa venda mista (R$ 200 cartao + R$ 55,54 pix) o total e maior que a fatia do Pix, e comparar com
// ele diria "Parcial" numa venda inteiramente paga. O total so entra como ultimo recurso, pra pedido
// anterior a DA-320 que nunca teve a coluna preenchida.
export interface PixDoPedido {
  /** Soma das linhas tipo 'pix' em `orcamento_pagamentos`. */
  declarado: number;
  /** Soma de `mp_vinculos` do orcamento (Pix de verdade, ja casado). */
  vinculado: number;
  /** `orcamentos.valor_pix_unico` — a linha de Pix congelada na finalizacao (DA-320). */
  valorPixUnico: number | null;
  /** `orcamentos.total`, so como ultimo recurso. */
  total: number | null;
}

/**
 * Quanto de Pix o pedido espera. Zero = a coluna nao tem o que dizer (mostra "—").
 * Declaracao presente manda sempre; ela e a intencao explicita do operador.
 */
export function pixEsperadoDoPedido(p: PixDoPedido): number {
  const declarado = cent(p.declarado);
  if (declarado > 0.005) return declarado;
  // Sem declaracao e sem dinheiro casado nao ha Pix nenhum — pedido comum em dinheiro/crediario.
  if (!(cent(p.vinculado) > 0.005)) return 0;
  const unico = cent(p.valorPixUnico ?? 0);
  return unico > 0.005 ? unico : Math.max(0, cent(p.total ?? 0));
}

// =============================================================================
// O TIPO DE NOTA MARCADO NO PEDIDO E' O PADRAO DO CAIXA, E O CAIXA PODE TROCAR (DA-877)
// =============================================================================
// Revoga a trava dura da [DA-432] (historia abaixo), por decisao dele em 03/11/2026: a trava nao
// tinha saida quando a vendedora nao estava (devolver ao vendedor reenvia no nome de quem
// devolve e a vendedora perde a comissao). O erro que ela fechava era o CLIQUE no botao gemeo ao
// lado; agora o tipo se escolhe numa CAIXA DE ESCOLHA que abre no tipo marcado, longe do botao
// de finalizar, e o rodape tem UM botao so que diz o tipo escolhido. Quem precisa de trava de
// verdade tem a do cadastro do cliente (abaixo). Medido em 03/11/2026: 90 dias, zero pedidos
// sem tipo marcado (1.564 Consumidor, 205 A4).

/** Com que tipo a caixa de escolha do caixa abre. A trava do cadastro vence a marcacao. */
export function tipoNotaInicial(
  marcado: TipoNotaCaixa,
  travarNotaA4: boolean | null | undefined,
  nfeIndisponivel: boolean,
): 'nfce' | 'nfe' {
  if (travarNotaA4) return 'nfe';
  if (nfeIndisponivel) return 'nfce';
  return marcado === 'nfe' ? 'nfe' : 'nfce';
}

// --- Historia: a trava dura da DA-432 (03/08/2026, revogada pela DA-877) ---
// A vendedora escolhe o tipo de nota (Consumidor / A4) ao enviar o pedido ao caixa — ela e quem
// falou com o cliente e sabe se ele precisa da nota com os dados dele. Ate 03/08/2026 o caixa
// exibia essa escolha (texto + anel amarelo no botao) mas deixava os DOIS botoes clicaveis, lado a
// lado, com o mesmo tamanho e a mesma cor. Um clique distraido emitia o tipo errado — e desfazer
// custa cancelar a venda na SEFAZ, que devolve o pedido ao caixa e arrasta a cauda inteira do
// cancelamento (foi o caso do ORC-006372 em 03/08).
//
// Decisao do Ruan: trava DURA, sem escape no caixa. Marcado o tipo, so ele finaliza; corrigir e
// devolver o pedido ao vendedor (botao ↺ na fila) pra ele remarcar e reenviar. A alternativa de
// liberar com senha/admin foi recusada — no balcao o escape vira o caminho normal, e o aviso que
// aparece sempre deixa de ser lido (a licao do selo que grita atoa, principio 1 do CLAUDE.md).
//
// Pedido SEM tipo marcado (`null`) segue livre: nao ha escolha a respeitar, e obrigar a marcacao
// seria uma regra nova sobre o vendedor, nao o conserto pedido.
//
// 'troca' e a Venda CM nao passam por aqui: nao sao "qual nota sai", sao outro desfecho.
export type TipoNotaCaixa = 'nfce' | 'nfe' | 'troca' | null | undefined;

export function nomeTipoNota(t: TipoNotaCaixa): string {
  return t === 'nfe' ? 'Nota A4' : t === 'nfce' ? 'Nota Consumidor' : 'sem tipo definido';
}

// =============================================================================
// O CLIENTE PODE ESTAR TRAVADO EM A4 (cadastro)
// =============================================================================
// Irma da trava acima, com a fonte trocada: la quem manda e' a marcacao do PEDIDO (a vendedora
// falou com o cliente), aqui e' o CADASTRO do cliente — a empresa que so aceita NF-e, sempre.
//
// Nasceu em 01/09/2026: a ILUMINAR levou um CUPOM (NFC-e 1968, R$ 119,90) numa venda que sempre
// sai em A4 — 129 NF-e contra aquele 1 cupom. Passados os 30 minutos de cancelamento da NFC-e nao
// ha como trocar o modelo ([DA-245]), e a nota errada fica: mesma assimetria de custo da [DA-432],
// um segundo pra errar contra um caminho que nao existe pra desfazer.
//
// ⚠️ Por que um CAMPO no cadastro e nao uma deducao de "Pessoa juridica": medido em 01/09/2026,
// dos 27 clientes com CNPJ que ja receberam nota, **18 so tomaram A4, 4 so tomaram CUPOM e 5
// misturam**. Deduzir de PJ barraria 70 cupons legitimos — o sistema nao sabe, so o Ruan sabe.
//
// ⛔ **Sem tipo marcado (`null`) NAO passa quando o cliente e' travado** — e' a diferenca em
// relacao a [DA-432], onde o null segue livre. La o null significa "nao ha escolha a respeitar";
// aqui existe uma escolha, e ela e' do cadastro: deixar passar sem marcacao devolveria a decisao
// pro caixa, que desde a DA-877 escolhe qualquer tipo — o cupom voltaria pela porta
// que esta trava existe pra fechar.
//
// 'troca' passa: venda 100% Credito da Loja nao emite nota nenhuma ([DA-369]), entao nao ha
// modelo a travar — mesmo desfecho que a [DA-432] ja tratava.

/** O cliente travado em A4 aceita sair como `tentado`? */
export function tipoNotaDoClientePermitido(
  travarNotaA4: boolean | null | undefined,
  tentado: TipoNotaCaixa,
): boolean {
  if (!travarNotaA4) return true;
  if (tentado === 'troca') return true;
  return tentado === 'nfe';
}

/** Recusa legivel pra quem esta com o cliente na frente — diz o motivo E a saida. */
export function mensagemClienteTravadoA4(clienteNome?: string | null): string {
  const quem = clienteNome ? `${clienteNome} esta` : 'Este cliente esta';
  return `${quem} travado em Nota A4 no cadastro: esta venda so pode sair como Nota A4 (NF-e). `
    + 'Pra liberar o Consumidor, desmarque "Travar Nota em A4" no cadastro do cliente.';
}

// ---------------------------------------------------------------------------
// DA-447 — RECEBIMENTO PARCIAL: o Pix vinculado tem que ser consumido por inteiro.
// ---------------------------------------------------------------------------

/**
 * Teto da sobra TOLERADA, como fracao do valor da venda. [DA-560]
 *
 * A trava nasceu contra um caso real de dono errado: venda de R$ 14,01 quitada com um Pix de
 * R$ 206,00 do Nubank, de outra pessoa — sobra de **1.370%** da venda. Em 17/08/2026 apareceu o
 * primeiro caso do outro lado: venda de R$ 605,01 paga com R$ 608,00, sobra de **0,49%** — o
 * cliente arredondou pra cima. Os dois estao a tres ordens de grandeza de distancia, entao a linha
 * nao precisa ser fina: 10% separa com folga enorme dos dois lados.
 */
export const SOBRA_PIX_TOLERADA_PCT = 0.10;

/**
 * A sobra segura a finalizacao E a emissao da nota? Regra UNICA — ate 17/08/2026 a mesma pergunta
 * era respondida em dois lugares com a conta escrita duas vezes (aqui e no `decidirEmissao...`), e
 * afrouxar so um deles faria a venda fechar com a nota presa: a pendencia mudaria de roupa, nao
 * sumiria.
 *
 * Tres degraus:
 *  - sobra dentro do centavo da calibracao (o cliente que paga o redondo) — nunca contou;
 *  - sobra PEQUENA perto da venda: **avisa e deixa passar**. Nao esconde nada — o recebimento fica
 *    `nao conferido` com o saldo disponivel e a faixa da Conciliacao segue contando ele. Ela so
 *    para de segurar uma venda que esta certa;
 *  - sobra GRANDE perto da venda: bloqueia como sempre. E a assinatura do dinheiro de outro cliente,
 *    e ai vale barrar com o cliente ainda na frente da operadora (a decisao original do Ruan).
 *
 * `totalDaVenda <= 0` cai no comportamento antigo (qualquer sobra bloqueia): sem base pra comparar,
 * a regra falha FECHADA.
 */
export function recebimentoParcialBloqueia(
  sobra: number | null | undefined,
  totalDaVenda: number | null | undefined,
): boolean {
  const s = Math.round((Number(sobra) || 0) * 100) / 100;
  if (s <= TOLERANCIA_CENTAVO_CALIBRACAO) return false;
  const base = Math.round((Number(totalDaVenda) || 0) * 100) / 100;
  if (base <= 0) return true;
  return s > Math.round(base * SOBRA_PIX_TOLERADA_PCT * 100) / 100;
}

/**
 * Recusa unica da finalizacao quando sobra dinheiro sem dono no Pix vinculado ao pedido.
 *
 * Diz os TRES numeros (o Pix inteiro, o que este pedido consome, o que sobra) e o caminho de saida.
 * Barrar sem dizer quanto sobra e onde resolver transforma a regra em tentativa e erro com o cliente
 * na frente — mesma licao da recusa de markup (DA-445).
 */
export function mensagemRecebimentoParcial(
  totalRecebimento: number,
  consumidoPeloPedido: number,
  sobra: number,
): string {
  return `Este pedido esta amarrado a um Pix de ${formatMoeda(totalRecebimento)}, mas consome so `
    + `${formatMoeda(consumidoPeloPedido)} — sobram ${formatMoeda(sobra)} sem dono nesse recebimento.\n\n`
    + `Sobra e sinal de que o Pix pode ser de outro cliente. Antes de fechar a venda, resolva na aba `
    + `Conciliacao Bancaria: vincule os outros pedidos que esse Pix cobre, ou troque pelo Pix no valor `
    + `deste pedido.`;
}

// =============================================================================
// DA-549 — O Pix que PROVA a transferencia entre contas da loja (cartao RDS caixa #22)
// =============================================================================
// Quando a loja manda dinheiro de uma conta propria pra outra, o sistema via as duas pontas sem
// saber que eram a mesma: a transferencia lancada de um lado, e o Pix caindo na fila da Conciliacao
// do outro, indistinguivel de dinheiro de cliente.
//
// A [DA-548] tentou resolver pedindo ao operador que DECLARASSE a natureza numa lista de quatro
// opcoes, e foi revogada no mesmo dia: tres daquelas opcoes ja tinham porta melhor na casa, e a
// unica sem porta e esta. Aqui a natureza nao e digitada — ela e consequencia de um FATO amarrado.
//
// Decisao PURA, usada nos dois lados (a mesma doutrina que sobreviveu da DA-548 p.4): o front
// usa pra nao oferecer o que o servidor recusaria e pra escrever o motivo ao lado do botao; o
// backend usa pra recusar de verdade. Duas copias divergiriam no primeiro ajuste.

export interface PixCandidatoAVinculo {
  /** `mp_recebimentos.status` — so 'approved' e dinheiro que de fato entrou. */
  status: string | null | undefined;
  /** Ja saiu da fila da Conciliacao por outro caminho. */
  conferido: boolean;
  /** Ja amarrado a uma transferencia (a unique parcial da 0229 cobre isso no banco). */
  transferencia_id: number | null | undefined;
  /** Quanto ja voltou pro pagador. Devolucao contradiz "isto foi transferencia nossa". */
  valor_devolvido: number | null | undefined;
  /** Quantos pedidos ja consomem este Pix (`mp_vinculos`). */
  vinculos: number;
}

/**
 * Este Pix pode ser amarrado a uma transferencia? `null` = pode; senao, a frase que o operador
 * precisa ler. Cada recusa nomeia a PORTA certa — a licao mais cara da DA-548 foi ter construido
 * opcao cuja ajuda honesta era "prefira outro botao".
 */
export function motivoRecusaVinculoPix(rec: PixCandidatoAVinculo): string | null {
  if ((rec.status ?? '') !== 'approved') {
    return 'Este Pix nao esta aprovado no Mercado Pago — so dinheiro confirmado prova uma transferencia.';
  }
  if (rec.transferencia_id != null) {
    return `Este Pix ja esta amarrado a transferencia #${rec.transferencia_id}. Desvincule la antes de usa-lo aqui.`;
  }
  if (rec.conferido) {
    return 'Este Pix ja foi conferido e saiu da fila da Conciliacao. Desfaca a conferencia primeiro, se ele for mesmo a sua transferencia.';
  }
  if (cent(rec.valor_devolvido ?? 0) > 0) {
    return 'Este Pix tem devolucao registrada — dinheiro que voltou pro pagador nao e transferencia entre contas da loja.';
  }
  if ((Number(rec.vinculos) || 0) > 0) {
    return 'Este Pix ja esta vinculado a pedido de cliente. Se o vinculo estiver errado, desfaca na Conciliacao Bancaria.';
  }
  return null;
}

/**
 * O valor da transferencia tem que ser EXATAMENTE o do Pix — o campo e espelho do recebimento,
 * nao palpite. Comparacao em centavos inteiros, nunca com epsilon abaixo da unidade da moeda
 * (a armadilha da DA-418: fracao de centavo que nenhum total denuncia).
 */
export function valorDaTransferenciaBateComPix(valorTransferencia: number, valorPix: number): boolean {
  return Math.round((Number(valorTransferencia) || 0) * 100) === Math.round((Number(valorPix) || 0) * 100);
}

// ---------------------------------------------------------------------------------------------
// Casamento das linhas de PIX com os recebimentos ja vinculados ao pedido ([DA-322], [DA-570])
// ---------------------------------------------------------------------------------------------

export interface LinhaPixParaCasar {
  /** id da linha de pagamento no modal (chave do resultado). */
  id: string;
  /** Quanto a linha PEDE. */
  valor: number;
}

export interface RecebimentoPixParaCasar {
  /** `mp_vinculos.valor` — quanto DESTE recebimento cobre o pedido (nao o valor cheio do Pix). */
  valor: number;
  pagador: string | null;
}

/** Por linha: quanto dela ja esta pago em Pix, e por quem. */
export type CasamentoPixPorLinha = Record<string, { valor: number; pagador: string | null }>;

/**
 * Decide, por linha de Pix, quanto dela o dinheiro que JA CAIU cobre.
 *
 * Regra pura porque ela ja errou em producao valendo uma venda travada: no ORC-006797 o cliente
 * pagou R$ 2,99 (troco de uma compra anterior) + R$ 372,02 numa unica linha de R$ 375,01, o
 * casamento antigo era de UM recebimento por linha e pegou o primeiro em ordem cronologica — a
 * linha caiu pra R$ 2,99 e o caixa anunciou "FALTA R$ 372,02" num pedido quitado ([DA-570]).
 *
 * Dois passes, nesta ordem, e a ordem e' a regra:
 *  1. **Exato** pra TODAS as linhas. Linha a linha, a primeira consumiria por aproximacao o
 *     recebimento que casava exato com a segunda, e as duas sairiam erradas.
 *  2. **Acumulado**, do MAIOR pro menor, enquanto couber na linha. Maior primeiro porque a ordem
 *     cronologica desperdica: linha de 100 com Pix de 30 e 90 ficaria com 30 e pediria 70 ja pagos.
 *
 * O que ESTOURA a linha nao entra — sobra em recebimento e' assunto da conciliacao
 * ([DA-447]/[DA-560]/[DA-569]), nunca absorvida calada aqui.
 */
export function casarPixRecebidos(
  linhas: LinhaPixParaCasar[],
  recebimentos: RecebimentoPixParaCasar[],
): CasamentoPixPorLinha {
  const casado: CasamentoPixPorLinha = {};
  if (!linhas.length || !recebimentos.length) return casado;
  const usados = new Set<number>();

  for (const l of linhas) {
    const idx = recebimentos.findIndex(
      (r, i) => !usados.has(i) && Math.abs(r.valor - l.valor) < TOLERANCIA_CENTAVO_CALIBRACAO,
    );
    const rec = idx < 0 ? undefined : recebimentos[idx];
    if (!rec) continue;
    usados.add(idx);
    casado[l.id] = { valor: rec.valor || l.valor, pagador: rec.pagador ?? null };
  }

  const porValorDesc = recebimentos
    .map((r, i) => ({ i, valor: r.valor, pagador: r.pagador ?? null }))
    .sort((a, b) => b.valor - a.valor);

  for (const l of linhas) {
    if (casado[l.id]) continue;
    let soma = 0;
    let pagador: string | null = null;
    for (const c of porValorDesc) {
      if (usados.has(c.i) || c.valor <= 0.005) continue;
      if (soma + c.valor > l.valor + TOLERANCIA_CENTAVO_CALIBRACAO) continue;
      usados.add(c.i);
      soma = +(soma + c.valor).toFixed(2);
      pagador = pagador ?? c.pagador;
    }
    if (soma > 0.005) casado[l.id] = { valor: soma, pagador };
  }

  return casado;
}

/**
 * Ainda ha Pix a COBRAR nesta venda? (declarado menos o que ja esta confirmado)
 *
 * Existe pra responder a pergunta certa onde antes se perguntava *"tem Pix nas formas?"* ([DA-626]).
 * O centavo quebrado da calibracao ([DA-233]) e' uma MARCA pro Pix de fora casar sozinho — e ela so
 * serve pro Pix que **ainda vai chegar**. Quando todo o Pix da venda ja caiu e esta vinculado, o
 * centavo nao identifica mais nada e vira um valor que o cliente nao tem como pagar: o pedido pede
 * R$ 102,01, ele entrega R$ 102,00, e a trava de gravacao ([DA-531], igualdade exata) recusa a venda.
 *
 * Sem linha de Pix nenhuma devolve `false` — que e' exatamente o comportamento antigo do
 * `deveDescalibrar`, preservado de proposito.
 *
 * ⚠️ NAO confundir com a trava do fechamento (`pixBloqueiaFinalizar` / `reunirParcelasEletronicas`),
 * que faz a MESMA conta pra outra decisao: la a sobra a receber IMPEDE fechar, aqui ela decide se o
 * centavo da calibracao fica de pe. Mesma pergunta, dois usos.
 */
export function restaPixACobrar(
  declarado: number,
  confirmado: number,
  tolerancia: number = TOLERANCIA_CENTAVO_CALIBRACAO,
): boolean {
  return cent(cent(declarado) - cent(confirmado)) > tolerancia;
}

/**
 * O centavo da calibracao deve ser DESFEITO nesta venda? — PURA ([DA-629]).
 *
 * Duas condicoes, e a segunda e' a que faltava. A [DA-626] respondeu so a primeira
 * (`restaPixACobrar`) e com isso abracou dois casos OPOSTOS sob o mesmo "nao resta Pix a cobrar":
 *
 *  - Pix PARCIAL ja pago, resto em dinheiro/cartao (o caso dela: R$ 2,00 caidos num total de
 *    102,01). O centavo ficou orfao no que AINDA falta receber, e o cliente nao tem como entregar
 *    R$ 100,01 em especie. **Desfaz.**
 *  - Pix INTEGRAL ja pago no valor calibrado (R$ 200,01 caidos num total de 200,01). Aqui o centavo
 *    NAO esta pendurado em nada a receber: ele ja entrou na conta da loja. Baixar o total pra
 *    200,00 deixa a linha confirmada (fixa desde a [DA-626] p.8) sobrando 1 centavo, e a trava de
 *    gravacao ([DA-531], igualdade exata) recusa a venda depois do clique. **Nao desfaz.**
 *
 * A regra que separa os dois e' o que o desfazer deixaria a receber: se o total natural cair ABAIXO
 * do Pix ja confirmado, o centavo esta dentro de dinheiro que ja entrou — e dinheiro que entrou nao
 * se desfaz (mesma guarda que a [DA-628] p.5 escreveu pro lado da cobranca parcial).
 *
 * Sem bump nenhum (total ja na grade) devolve `false`: nao ha o que desfazer.
 * Sem Pix nenhum (declarado e confirmado zerados) devolve `true` — o comportamento antigo do
 * `deveDescalibrar`, preservado de proposito.
 *
 * [DA-807] `cartaoJaPago` = cartao ja APROVADO na maquininha pra este pedido. Mesmo argumento do Pix
 * integral: o caixa troca a forma de Pix pra cartao, a tela cobra o total que mostra (89,01, com o
 * centavo) e a maquininha aprova. Dali em diante o centavo esta dentro de dinheiro que ja entrou, e
 * desfaze-lo deixava o pedido em 89,00 contra um cartao de 89,01 — a trava da [DA-531] recusava a
 * venda e o operador nao tinha saida no caixa (ORC-008648 em 05/10, ORC-008826 em 10/10).
 */
export function deveDesfazerCentavoCalibrado(
  totalCalibrado: number,
  pixDeclarado: number,
  pixConfirmado: number,
  cartaoJaPago = 0,
): boolean {
  if (restaPixACobrar(pixDeclarado, pixConfirmado)) return false;
  const total = cent(totalCalibrado);
  const natural = cent(descalibrarTotalCalibrado(total));
  if (!(natural < total)) return false;
  return natural >= cent(cent(pixConfirmado) + cent(cartaoJaPago));
}

/** O que fazer com a linha declarada diante do Pix que ja caiu nela. */
export type DivisaoLinhaPix =
  /** Nada recebido aqui: a linha segue como esta, livre pro operador. */
  | { tipo: 'intacta' }
  /** O recebido cobre a linha inteira (dentro do centavo): ela vira o fato, e nada sobra a cobrar. */
  | { tipo: 'coberta' }
  /** O recebido cobre PARTE: vira duas linhas — o que entrou (travado) e o que falta (livre). */
  | { tipo: 'partir'; recebido: number; resto: number };

/**
 * Decide se a linha de Pix declarada vira UMA linha ou DUAS, diante do que ja foi conciliado.
 *
 * Existe porque encolher a linha pro valor recebido esconde o trabalho que sobra ([DA-625]). No
 * ORC-007160 (R$ 102,01 declarado em Pix, R$ 2,00 pagos e vinculados) o caixa abria com uma linha
 * so — `PIX 2,00` — e os R$ 100,01 que ainda faltavam nao tinham onde ser lancados a nao ser
 * clicando em "Adicionar forma de pagamento", que ninguem adivinha. O gesto natural do operador e'
 * mexer na linha que esta na tela: trocar aquela linha pra DINHEIRO soltava o casamento e a venda
 * passava a cobrar R$ 2,00 que ja estavam na conta da loja. Partindo, a linha que ele mexe ja nasce
 * com o que falta, e a que registra o dinheiro recebido fica fora do caminho.
 *
 * A tolerancia e' a mesma de todo o resto do fechamento (5 centavos, a variacao do QR estatico):
 * recebido a um centavo do declarado e' linha COBERTA, nao uma partida com resto de R$ 0,01 — um
 * resto assim viraria uma linha de pagamento que ninguem consegue cobrar.
 */
export function dividirLinhaPorPixRecebido(
  valorLinha: number,
  valorCasado: number,
  tolerancia: number = TOLERANCIA_CENTAVO_CALIBRACAO,
): DivisaoLinhaPix {
  const linha = cent(valorLinha);
  const casado = cent(valorCasado);
  if (casado <= 0.005) return { tipo: 'intacta' };
  // Cobre tudo (ou mais — recebimento maior nao deveria chegar aqui, mas se chegar a sobra e'
  // assunto da conciliacao, nunca uma linha negativa na tela).
  if (casado >= cent(linha - tolerancia)) return { tipo: 'coberta' };
  return { tipo: 'partir', recebido: casado, resto: cent(linha - casado) };
}
